// Mock data for Growth Talent Group website

export const mockBlogs = [
  {
    id: '1',
    title: 'Top 5 Skills Property Managers Need in 2025',
    excerpt: 'The property management landscape is evolving rapidly. Discover the essential skills that will set you apart in today\'s competitive market.',
    content: `
# Top 5 Skills Property Managers Need in 2025

The property management industry is evolving at an unprecedented pace. With new technologies, changing tenant expectations, and increased regulatory requirements, property managers need to adapt and develop new skills to thrive in 2025 and beyond.

Having worked in property recruitment for over 15 years, I've seen firsthand which skills separate good property managers from exceptional ones. Here are the five critical skills every property manager should master this year.

## 1. Digital Proficiency and PropTech Savviness

Gone are the days when basic computer literacy was enough. Modern property managers must be comfortable with:

- **Property management software** (PropertyMe, Console, Rex)
- **Virtual inspection tools** and 3D tour platforms
- **Cloud-based documentation systems**
- **Digital payment platforms**
- **Communication apps** for tenant engagement

The best property managers I've placed recently are those who embrace technology rather than resist it. They understand that PropTech isn't replacing them—it's freeing them up to focus on relationship building and strategic planning.

## 2. Advanced Communication Skills

Communication has always been important, but in 2025, it's more nuanced than ever. Property managers need to:

- Navigate difficult conversations with diplomacy
- Communicate effectively across multiple channels (email, phone, text, video)
- Translate complex legislation into plain English for landlords and tenants
- Manage expectations proactively
- Build rapport with diverse stakeholders

I regularly see talented property managers miss out on opportunities simply because they can't articulate their value in interviews. Strong communication skills aren't just about doing the job—they're about advancing your career.

## 3. Legislative Knowledge and Compliance Expertise

With constant changes to tenancy legislation across Australian states, compliance knowledge is now a critical differentiator. Property managers must:

- Stay current with state-specific tenancy laws
- Understand bond lodgement requirements
- Navigate repair and maintenance obligations
- Implement proper notice periods
- Maintain compliant documentation

The property managers who invest time in professional development and stay ahead of legislative changes are the ones commanding premium salaries. This isn't optional anymore—it's essential.

## 4. Financial Acumen and Reporting Skills

Modern property managers are expected to be financial advisors to their landlords. This requires:

- Understanding market trends and rental yields
- Preparing detailed financial reports
- Budgeting for property maintenance
- Advising on tax implications
- Maximising rental returns while maintaining property standards

The most successful property managers I work with can speak confidently about cash flow, depreciation schedules, and market analysis. They're business partners, not just administrators.

## 5. Emotional Intelligence and Problem-Solving

Perhaps the most underrated skill is emotional intelligence. Property management is fundamentally about people, and the ability to:

- Read situations and respond appropriately
- Remain calm under pressure
- Empathise with tenant concerns while protecting landlord interests
- Find creative solutions to complex problems
- Build long-term relationships based on trust

This is the skill that creates career longevity. Technical skills can be learned, but emotional intelligence and problem-solving ability are what transform a property manager into an invaluable asset.

## The Path Forward

If you're serious about advancing your property management career in 2025, focus on developing these five skills. They're what employers are looking for, and they're what will set you apart in a competitive market.

At Growth Talent Group, we see hundreds of property management roles every month, and these skills consistently appear in job descriptions for the best positions with the highest salaries.

**Ready to take your property management career to the next level?** [Get in touch](/contact) with our team to discuss opportunities that match your skills and aspirations.

*Brandon Wylie is Director of Growth Talent Group with 15 years of experience in property recruitment.*
    `,
    author: 'Brandon Wylie',
    category: 'Property Management',
    date: '2025-04-01',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=500&fit=crop',
    featured: true
  },
  {
    id: '2',
    title: 'Navigating Your Career Transition into Strata Management',
    excerpt: 'Thinking about a career in strata management? Here\'s everything you need to know about making a successful transition.',
    content: `
# Navigating Your Career Transition into Strata Management

Making a career change can be daunting, but if you're considering a move into strata management, you're looking at one of the most rewarding and stable career paths in the property sector.

Over my 5 years in recruitment, I've helped dozens of professionals successfully transition into strata management from various backgrounds. Here's your complete guide to making that move.

## Why Strata Management?

Strata management offers several compelling advantages:

- **Job security** - With millions of Australians living in strata properties, demand is only growing
- **Competitive salaries** - Entry-level positions start at $55K-$65K, with experienced managers earning $80K-$120K+
- **Career progression** - Clear pathways from Assistant to Senior Manager to Portfolio Manager
- **Work-life balance** - Generally 9-5 with minimal weekend work
- **Variety** - Every building is different, every owner has unique needs

## Who Makes a Good Strata Manager?

The best strata managers I've placed come from diverse backgrounds, but they share common traits:

**Strong Candidates Often Have:**
- Customer service experience (essential!)
- Administrative or project coordination skills
- Property management, real estate, or facilities management background
- Strong communication and conflict resolution abilities
- Attention to detail and organised work habits

**Don't worry if you're not from a property background.** I've successfully placed candidates from retail management, hospitality, event coordination, and even teaching. What matters most is your transferable skills.

## Understanding the Role

Before making the leap, understand what strata managers actually do:

**Daily Responsibilities Include:**
- Managing owner corporation finances and budgets
- Coordinating building maintenance and repairs
- Organising AGMs and committee meetings
- Ensuring building compliance with regulations
- Resolving owner and tenant disputes
- Liaising with contractors and service providers
- Maintaining accurate records and correspondence

It's part finance, part customer service, part project management, and part diplomacy. If that mix appeals to you, you'll probably love strata management.

## Required Qualifications

Here's what you need to know about qualifications:

**Minimum Requirements (vary by state):**
- Certificate IV in Strata Community Management (highly recommended)
- Relevant work experience (especially in property or customer service)
- Current driver's licence (for property inspections)

**Helpful but not essential:**
- Real Estate licence
- Diploma of Strata Community Management
- Financial management qualifications

Many employers will hire motivated candidates and support them through their Certificate IV qualification. Don't let lack of qualifications stop you from applying!

## Making the Transition

**Step 1: Research and Learn**
- Read industry publications and websites
- Join SCA (Strata Community Australia) as an associate member
- Follow strata management companies on LinkedIn
- Understand the challenges and rewards

**Step 2: Get Qualified**
- Enrol in Certificate IV in Strata Community Management
- Many courses offer online or part-time study
- Some employers will sponsor your qualification

**Step 3: Gain Relevant Experience**
- If currently employed, look for opportunities to manage projects, handle complaints, or deal with contractors
- Volunteer for committee work in your own building
- Consider taking an assistant role first

**Step 4: Craft Your Application**
- Highlight transferable skills (customer service, organisation, financial literacy)
- Demonstrate your understanding of the role
- Show enthusiasm and willingness to learn
- Include any relevant property experience

**Step 5: Prepare for Interviews**
- Research the company and their portfolio
- Prepare examples of conflict resolution
- Demonstrate problem-solving abilities
- Ask informed questions about their training program

## What Employers Look For

In my experience, strata management companies value:

1. **Communication skills** - You'll deal with diverse personalities daily
2. **Organisation** - Managing multiple buildings requires excellent systems
3. **Resilience** - Handling complaints and conflicts requires thick skin
4. **Initiative** - Proactive problem-solving is highly valued
5. **Tech-savviness** - Comfort with software and digital tools

## Salary Expectations

Here's what you can realistically expect:

- **Assistant Strata Manager:** $50K-$60K
- **Strata Manager (1-2 years):** $60K-$75K
- **Strata Manager (3-5 years):** $75K-$95K
- **Senior Strata Manager:** $95K-$120K+
- **Portfolio Manager:** $120K-$150K+

Plus many companies offer bonuses, car allowances, and professional development support.

## Common Mistakes to Avoid

**Don't:**
- Underestimate the complexity of the role
- Expect it to be easy because you've lived in a strata building
- Ignore the importance of financial literacy
- Apply without researching the company
- Give up if your first application is unsuccessful

## Your Next Steps

If you're serious about transitioning into strata management:

1. Start your Certificate IV now (don't wait)
2. Update your CV to highlight relevant skills
3. Network with strata managers on LinkedIn
4. Apply for assistant roles to get your foot in the door
5. [Contact our team](/contact) for personalised career advice

The strata management industry is crying out for quality candidates. With the right approach and preparation, you could be starting your new career within 3-6 months.

**Ready to explore strata management opportunities?** Our team specialises in strata recruitment and can guide you through the transition process. [Get in touch today](/contact).

*Madeleine Mulholland is Director at Growth Talent Group with 5 years of experience in property and strata recruitment.*
    `,
    author: 'Madeleine Mulholland',
    category: 'Strata Management',
    date: '2025-03-28',
    readTime: '10 min read',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=500&fit=crop',
    featured: true
  },
  {
    id: '3',
    title: 'Real Estate & Property Salary Guide 2025: What to Expect',
    excerpt: 'Comprehensive salary benchmarks for property and real estate professionals across Australia.',
    content: `
# Real Estate & Property Salary Guide 2025: What to Expect

Understanding salary benchmarks is crucial whether you're looking for your next role, negotiating a raise, or hiring new team members. As Director and Head of Strata at Growth Talent Group, I've analysed hundreds of job offers and salary packages over the past decade.

Here's your comprehensive guide to property and real estate salaries in 2025.

## Market Overview

The Australian property recruitment market in 2025 is characterised by:

- **Strong demand** for qualified property and strata managers
- **Salary inflation** of 5-8% across most roles
- **Skill shortages** particularly in strata management
- **Remote work** options adding flexibility to packages
- **Increased bonuses** and performance incentives

## Property Management Salaries

### Assistant Property Manager
**Salary Range:** $45,000 - $55,000 + super

**Responsibilities:** Managing 50-100 properties under supervision, handling maintenance requests, routine inspections, tenant communications.

**What increases your value:**
- 6+ months experience
- Proficiency in property management software
- Strong communication skills
- Relevant qualifications (Certificate IV)

### Property Manager
**Salary Range:** $60,000 - $85,000 + super

**Responsibilities:** Full portfolio management (100-150 properties), tenant acquisition, owner reporting, compliance management.

**Salary factors:**
- **Entry level (0-2 years):** $60K-$68K
- **Experienced (3-5 years):** $70K-$80K
- **Senior (5+ years):** $80K-$85K

**Additional benefits:** Company car or allowance ($5K-$10K), phone, laptop, annual leave loading

### Senior Property Manager / Portfolio Manager
**Salary Range:** $85,000 - $110,000 + super + bonuses

**Responsibilities:** Large portfolios (150-250+ properties), team leadership, business development, strategic client relationships.

**What commands top dollar:**
- Proven track record of portfolio growth
- Client retention rates above 90%
- Leadership experience
- BDM capabilities

**Typical packages include:** Company car, performance bonuses (10-20% of base), profit sharing, professional development budget

### Property Management Team Leader / Department Manager
**Salary Range:** $100,000 - $140,000 + super + bonuses

**Responsibilities:** Team management, recruitment, training, departmental P&L, strategic planning.

**Package components:**
- Base salary: $100K-$120K
- Performance bonuses: $15K-$30K
- Company vehicle or allowance
- Additional leave entitlements

## Strata Management Salaries

### Assistant Strata Manager
**Salary Range:** $50,000 - $62,000 + super

**Responsibilities:** Supporting senior managers, AGM preparation, basic correspondence, maintenance coordination for 20-40 buildings.

**Entry requirements:**
- Customer service experience
- Certificate IV (or willing to obtain)
- Strong organisational skills

### Strata Manager
**Salary Range:** $65,000 - $95,000 + super

**Experience breakdown:**
- **Junior (1-2 years):** $65K-$72K
- **Mid-level (3-5 years):** $75K-$85K
- **Senior (5+ years):** $85K-$95K

**Portfolio expectations:**
- Junior: 30-50 buildings
- Mid: 50-80 buildings
- Senior: 80-120 buildings

**Additional benefits:** Car allowance ($5K-$12K), mobile, laptop, parking, professional development support

### Senior Strata Manager
**Salary Range:** $95,000 - $125,000 + super + bonuses

**Responsibilities:** Complex portfolios (100-150 buildings), high-value buildings, mentoring junior staff, committee management.

**Premium factors:**
- Commercial building experience
- Large development experience
- Committee presentation skills
- Building defect management expertise

### Strata Portfolio Manager / Department Head
**Salary Range:** $120,000 - $160,000 + super + bonuses

**Responsibilities:** Team leadership, business development, client relationship management, strategic growth, P&L responsibility.

**Packages typically include:**
- Base: $120K-$145K
- Bonuses: $15K-$35K
- Company vehicle
- Equity/profit sharing (larger firms)
- Additional leave and benefits

## Real Estate Sales

### Sales Administrator / Sales Support
**Salary Range:** $50,000 - $65,000 + super

### Sales Agent (Residential)
**Package Range:** $60,000 - $150,000+ (base + commission)

**Structure:**
- Base salary: $50K-$70K
- Commission: 15-35% of GCI
- High performers: $120K-$300K+ total

### Sales Manager
**Package Range:** $90,000 - $200,000+ (base + override)

## Leasing Specialists

### Leasing Consultant
**Salary Range:** $55,000 - $75,000 + super + commission

**Commission structures:**
- Per lease: $100-$300
- Annual packages: $65K-$85K all-inclusive

### Leasing Manager
**Salary Range:** $75,000 - $95,000 + super + bonuses

## Regional Variations

**Sydney:** Generally 10-15% higher than national average
**Melbourne:** 5-10% higher than national average
**Brisbane/Perth/Adelaide:** Aligned with national average
**Regional:** Typically 10-20% lower but with lower living costs

## Additional Remuneration Trends

**Popular Benefits in 2025:**
- Flexible working arrangements (2-3 days WFH)
- Professional development allowances ($2K-$5K)
- Mental health days (1-2 per year)
- Birthday leave
- Extra annual leave purchase schemes
- Wellness programs and gym memberships
- Technology allowances
- Increased parental leave entitlements

## What Impacts Your Salary?

**Factors that increase earning potential:**
1. Relevant qualifications and ongoing professional development
2. Proven track record and references
3. Specialised skills (building defects, commercial, high-rise)
4. Leadership and mentoring abilities
5. Business development skills
6. Technology proficiency
7. Multi-state experience

## Negotiation Tips

**Do:**
- Research market rates thoroughly
- Quantify your achievements
- Consider the full package, not just base salary
- Be prepared to demonstrate your value
- Know your walk-away point

**Don't:**
- Anchor too low in initial discussions
- Accept the first offer without consideration
- Forget to negotiate benefits beyond salary
- Compare yourself to colleagues (focus on market rates)

## Looking Ahead

**Predictions for 2025-2026:**
- Continued salary growth (4-7% annually)
- Increased competition for experienced property managers
- Growing emphasis on mental health and wellbeing benefits
- More flexible work arrangements becoming standard
- Technology skills becoming mandatory, not optional

## How We Can Help

At Growth Talent Group, we specialise in property and strata recruitment and can provide:
- Personalised salary benchmarking
- Career progression advice
- CV optimisation
- Interview preparation
- Salary negotiation support

**Ready to discuss your next career move?** [Contact our team](/contact) for a confidential conversation about your options and earning potential.

*Steven Falkiner is Director and Head of Strata at Growth Talent Group with 10 years of experience in property and strata recruitment.*
    `,
    author: 'Steven Falkiner',
    category: 'Industry Insights',
    date: '2025-03-20',
    readTime: '12 min read',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=500&fit=crop',
    featured: false
  },
  {
    id: '4',
    title: 'How to Ace Your Property Management Interview',
    excerpt: 'Expert tips and common questions to help you prepare for your next property management interview.',
    content: `
# How to Ace Your Property Management Interview

So you've landed an interview for a property management role—congratulations! Now comes the crucial part: making sure you stand out from other candidates and demonstrate that you're the right fit for the position.

After 15 years placing property managers across Australia, I've debriefed hundreds of interviews and identified exactly what employers are looking for. Here's your comprehensive guide to interview success.

## Before the Interview: Preparation is Everything

The difference between good candidates and great candidates often comes down to preparation. Here's what you need to do:

### Research the Company Thoroughly

Don't just visit their website—dig deeper:

- **Understand their portfolio**: What types of properties do they manage? Residential, commercial, or both?
- **Know their values**: What does the company pride itself on? Client service? Innovation? Community?
- **Check their online presence**: Read their Google reviews, social media, and any recent news
- **Identify their competitive advantage**: What makes them different from other agencies?

When you can reference specific aspects of their business in your interview, it demonstrates genuine interest and initiative.

### Prepare Your Examples

Property management interviews are heavily focused on behavioural questions. Prepare specific examples of:

- **Conflict resolution**: Times you've successfully resolved disputes between parties
- **Problem-solving**: Situations where you've found creative solutions to challenges
- **Time management**: How you've handled competing priorities and tight deadlines
- **Client service**: Instances where you've gone above and beyond for clients
- **Attention to detail**: Examples of catching and fixing important errors

Use the STAR method (Situation, Task, Action, Result) to structure your responses. This keeps you focused and ensures you highlight your achievements clearly.

### Know Your Numbers

Be prepared to discuss:

- Size of portfolios you've managed
- Occupancy rates you've achieved
- Rent arrears percentages
- Client retention rates
- Your current portfolio value
- Number of inspections completed per week

Specific numbers demonstrate professionalism and accountability.

## Common Property Management Interview Questions

### 1. "Tell me about yourself"

This is usually the opening question. Keep it professional and relevant:

**Structure your response:**
- Brief background (how you got into property management)
- Current role and responsibilities
- Key achievements
- Why you're interested in this opportunity

**Keep it to 2-3 minutes maximum.** This isn't your life story—it's a professional snapshot.

### 2. "How do you handle difficult landlords or tenants?"

Employers want to know you can remain professional under pressure.

**Strong answers include:**
- Staying calm and listening actively
- Finding common ground and win-win solutions
- Setting clear expectations upfront
- Documenting everything professionally
- Knowing when to escalate issues appropriately

**Avoid:** Speaking negatively about previous clients or suggesting you always "give in" to demands.

### 3. "What property management software are you familiar with?"

Technology skills are increasingly important. Be honest about:

- Software you've used extensively (PropertyMe, Console, Rex, etc.)
- Your comfort level learning new systems
- Examples of how you've used technology to improve efficiency

**Pro tip:** If you're not familiar with their specific software, research it before the interview and mention your willingness to learn.

### 4. "How do you stay organised when managing multiple properties?"

This tests your systems and processes.

**Demonstrate you have:**
- A systematic approach to task prioritisation
- Calendar management and reminder systems
- Checklists for routine tasks
- Clear communication protocols
- Regular portfolio reviews

### 5. "Describe a time you made a mistake. How did you handle it?"

This assesses your accountability and problem-solving skills.

**Strong responses:**
- Acknowledge a genuine mistake (not "I'm too much of a perfectionist")
- Explain how you took ownership
- Describe steps you took to fix it
- Share what you learned and how you've prevented similar issues since

### 6. "Where do you see yourself in five years?"

Employers want to know you're ambitious but also realistic.

**Good approaches:**
- Express interest in growing within property management
- Mention specific skills you want to develop
- Show awareness of career progression paths
- Align your goals with opportunities at their company

## What to Wear

Property management sits between professional services and trades. Aim for:

**Men:**
- Business shirt and trousers (suit jacket optional but professional)
- Polished shoes
- Conservative colours

**Women:**
- Business attire or smart dress
- Professional shoes (comfort matters—you might be doing property tours)
- Minimal jewellery

**Everyone:**
- Well-groomed appearance
- Minimal perfume/cologne
- Portfolio or folder for documents

## Questions You Should Ask

Interviews are a two-way street. Prepare 3-5 thoughtful questions such as:

- "What does a typical day look like for someone in this role?"
- "What are the biggest challenges facing your property management department right now?"
- "How do you support ongoing professional development?"
- "What property management software and tools does the team use?"
- "What would success look like in this role after 6 months?"
- "How is the portfolio structured, and what support is available?"

**Avoid asking about salary, leave, or benefits in the first interview** unless the interviewer raises these topics.

## Red Flags to Watch For

While you're being interviewed, assess whether this is the right workplace for you:

- **Unrealistic portfolio sizes** (150+ residential properties per manager)
- **High staff turnover** (ask how long the current team has been there)
- **Unclear support structures** (no assistant support, limited training)
- **Outdated technology** (paper-based systems, no mobile access)
- **Dismissive attitudes** towards work-life balance

## After the Interview

### Follow Up Professionally

Within 24 hours, send a brief thank-you email:

- Express appreciation for their time
- Reiterate your interest in the role
- Mention one specific aspect of the conversation that excited you
- Keep it concise (3-4 sentences maximum)

### Reflect and Learn

Regardless of the outcome:

- Note questions you found difficult
- Identify areas for improvement
- Update your examples and responses
- Request feedback if you're unsuccessful

## Common Mistakes to Avoid

1. **Speaking negatively about current/previous employers**
   - Even if you had legitimate issues, keep it professional
   - Focus on what you're looking for (growth, challenge, etc.)

2. **Being vague or unprepared**
   - "I'm good with people" isn't enough—give specific examples
   - Know your numbers and achievements

3. **Not asking questions**
   - This suggests lack of genuine interest
   - Prepare questions even if you think you know everything

4. **Checking your phone**
   - Turn it off completely during the interview
   - Give your full attention

5. **Arriving unprepared**
   - Bring extra copies of your CV
   - Have references ready
   - Bring a notepad and pen

## Final Thoughts

The best interviews feel like professional conversations rather than interrogations. Be yourself, stay confident, and remember that employers want you to succeed—they're trying to fill a role just as much as you're trying to land one.

If you can demonstrate that you have:
- Strong communication skills
- Attention to detail
- Problem-solving abilities
- A customer service mindset
- Genuine interest in their company

You'll be well-positioned for success.

**Need help preparing for your next property management interview?** [Contact our team](/contact) for personalised coaching and career advice. We've helped hundreds of candidates successfully navigate the interview process.

*Brandon Wylie is Director of Growth Talent Group with 15 years of experience in property recruitment.*
    `,
    author: 'Brandon Wylie',
    category: 'Career Tips',
    date: '2025-03-15',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=500&fit=crop',
    featured: false
  },
  {
    id: '5',
    title: 'The Future of Real Estate Recruitment in Australia',
    excerpt: 'Exploring emerging trends and technologies shaping the future of recruitment in the property sector.',
    content: `
# The Future of Real Estate Recruitment in Australia

The property recruitment landscape is evolving faster than ever before. From AI-powered candidate matching to virtual onboarding, the way we find and place talent is transforming dramatically.

Having spent 5 years in property recruitment, I've witnessed significant shifts in how agencies hire, how candidates search for roles, and what both parties expect from the process. Here's what I believe the future holds.

## The Current State of Property Recruitment

Before looking forward, let's understand where we are:

**Traditional Challenges:**
- High staff turnover rates (30-40% annually in some agencies)
- Skills shortages, particularly in strata management
- Lengthy hiring processes (4-8 weeks average)
- Difficulty assessing cultural fit remotely
- Competition for experienced professionals

**What's Changing:**
The pandemic accelerated digital transformation, remote work normalisation, and shifted candidate expectations dramatically. These changes aren't temporary—they're permanent evolutions in how we work.

## Trend 1: Technology-Enhanced Recruitment

### AI and Machine Learning

Artificial intelligence is already transforming recruitment:

**Candidate Matching:**
- AI algorithms analyse CVs and job descriptions to identify best matches
- Predictive analytics assess candidate success probability
- Automated screening questions reduce initial review time

**Benefits:**
- Faster shortlisting (hours instead of days)
- Reduced unconscious bias in initial screening
- Better matches based on skills and experience patterns

**What This Means for You:**
- **Job seekers:** Ensure your CV is keyword-rich and clearly structured
- **Employers:** Human judgment still crucial—technology assists, doesn't replace

### Video Interview Platforms

Video interviewing has evolved beyond basic video calls:

- **One-way video interviews** where candidates record answers to preset questions
- **AI analysis** of communication skills, confidence, and responses
- **Scheduling flexibility** reducing coordination challenges

**The Reality:**
At Growth Talent Group, we've found video interviews excellent for initial screenings, but in-person meetings remain valuable for final-stage candidates. The future is hybrid, not fully virtual.

### Virtual Reality Onboarding

Some agencies are experimenting with VR for:
- Virtual office tours
- Immersive training simulations
- Remote property inspections practice

**Adoption Timeline:**
While exciting, widespread VR adoption in property recruitment is likely 3-5 years away. Early adopters are testing now.

## Trend 2: The Skills Revolution

### What Employers Will Value Most

**Technical Skills:**
- Advanced PropTech proficiency (no longer optional)
- Data analysis and reporting capabilities
- CRM and database management
- Digital marketing basics

**Soft Skills (Increasingly Critical):**
- Emotional intelligence and empathy
- Adaptability and learning agility
- Virtual communication effectiveness
- Self-management and accountability

**The Shift:**
We're moving from "years of experience" to "demonstrable skills and potential." A candidate with 2 years experience and strong tech skills may outperform someone with 10 years but outdated approaches.

### Micro-Credentials and Continuous Learning

Traditional qualifications remain important, but expect:

- **Micro-credentials** for specific skills (PropTech platforms, digital marketing, client relationship management)
- **Continuous professional development** as a hiring requirement
- **Skills-based assessments** during recruitment (practical tests, case studies)
- **Learning culture fit** as important as current skill level

## Trend 3: Flexible Working Models

### Hybrid is Here to Stay

The data is clear: flexible working isn't going away.

**What We're Seeing:**
- 60-70% of property management roles now offer some flexibility
- 2-3 days in office becoming standard
- Results-based performance metrics replacing "time in seat"
- Geographic boundaries expanding

**Implications:**
- **Candidates:** Negotiate flexibility upfront—it's expected now
- **Employers:** Clear policies and trust-based management essential
- **Recruiters:** Understanding remote work capabilities now part of assessment

### The Rise of Remote Property Management

Technology now enables:
- Virtual property inspections with photos and video
- Digital document signing and processing
- Online rent collection and payment
- Cloud-based communication with owners and tenants

**The Future:**
While property management will never be 100% remote (site visits remain necessary), we'll see:
- Reduced need for daily office presence
- Regional candidates accessing metro opportunities
- Broader talent pools for employers
- Location-independent career options

## Trend 4: Candidate Experience Expectations

### Recruitment as Marketing

Today's candidates research employers like consumers research products:

**They Expect:**
- Clear, honest job descriptions
- Transparent salary ranges
- Quick response times (within 48 hours)
- Respectful communication throughout
- Feedback regardless of outcome

**Poor Candidate Experience = Brand Damage:**
Negative reviews on Glassdoor, Google, and Seek impact future hiring. Companies that ghost candidates or drag out processes are increasingly called out publicly.

### Personalised Recruitment Journeys

One-size-fits-all approaches are dying. Future recruitment will feature:

- Customised communication based on candidate preferences
- Role-specific assessment processes
- Personalised onboarding plans
- Career pathway discussions from day one

## Trend 5: Diversity and Inclusion

### Beyond Compliance

Diversity initiatives are moving from "nice to have" to business imperative:

**Why Employers Care:**
- Diverse teams perform better (research-backed)
- Reflects diverse client bases
- Attracts top talent (especially younger generations)
- Reduces groupthink and improves decision-making

**What's Changing:**
- Blind CV screening (removing names, ages, photos)
- Diverse interview panels
- Inclusive job descriptions (removing gendered language)
- Flexibility policies supporting caregivers
- Cultural competency training

### Generational Diversity

For the first time, property management teams span four generations:

- **Baby Boomers** (experience and client relationships)
- **Gen X** (leadership and mentoring)
- **Millennials** (tech-savvy and innovative)
- **Gen Z** (digital natives and fresh perspectives)

Smart employers leverage strengths from each generation rather than viewing differences as challenges.

## Trend 6: Employer Branding

### Recruitment Marketing Sophistication

Companies are investing in employer brand like never before:

**Tactics Include:**
- Employee testimonial videos
- Day-in-the-life content on social media
- Company culture showcases
- Transparent salary and benefits information
- Career progression stories

**Why It Matters:**
Top candidates have choices. The agencies winning talent wars are those telling compelling stories about why people want to work there.

### Social Media Recruitment

LinkedIn is no longer enough. Expect:

- Instagram and TikTok for employer branding
- Employee advocacy programs (staff sharing opportunities)
- Influencer-style recruitment content
- Community building before hiring

## Trend 7: The Contractor Economy

### Rise of Fractional and Contract Roles

Some property professionals are choosing:

- Contract portfolio management (3-6 month engagements)
- Fractional roles (part-time arrangements with multiple agencies)
- Project-based work (special projects, digital transformations)

**Drivers:**
- Desire for variety and flexibility
- Higher hourly rates
- Portfolio career approaches
- Semi-retirement transitions

**For Agencies:**
This creates both challenges (retention) and opportunities (access to specialised skills without full-time commitment).

## Preparing for the Future

### For Job Seekers:

1. **Invest in technology skills** - They're no longer optional
2. **Build your personal brand** - LinkedIn presence, industry engagement
3. **Stay curious and adaptable** - Continuous learning mindset
4. **Develop soft skills** - They're harder to automate
5. **Network strategically** - Relationships still drive opportunities

### For Employers:

1. **Modernise your recruitment process** - Speed and experience matter
2. **Invest in employer branding** - You're competing for talent
3. **Embrace flexibility** - It's a competitive advantage
4. **Focus on development** - Grow your own talent
5. **Use data** - Track metrics, optimise constantly

### For Everyone:

The future of property recruitment is human-centric technology. Yes, AI and automation will handle administrative tasks, but the relationships, judgment, and nuanced understanding that great recruiters provide become MORE valuable, not less.

## The Bottom Line

Property recruitment in 2025 and beyond will be:
- Faster (technology-enabled)
- More flexible (hybrid and remote options)
- More transparent (salary, process, expectations)
- More candidate-centric (experience matters)
- More skills-focused (less emphasis on years, more on capabilities)

The agencies and professionals who embrace these changes will thrive. Those who resist will struggle.

**Want to discuss how these trends impact your career or hiring strategy?** [Get in touch with our team](/contact) for insights specific to your situation.

*Madeleine Mulholland is Director at Growth Talent Group with 5 years of experience in property and strata recruitment.*
    `,
    author: 'Madeleine Mulholland',
    category: 'Industry Insights',
    date: '2025-03-10',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=500&fit=crop',
    featured: false
  },
  {
    id: '6',
    title: 'Building a Successful Career in Strata Management',
    excerpt: 'A comprehensive guide to growing your career in the strata management industry.',
    content: `
# Building a Successful Career in Strata Management

Strata management offers one of the most rewarding and stable career paths in the property sector. With millions of Australians living in strata properties and the sector growing year-on-year, opportunities for career progression are exceptional.

As Director and Head of Strata at Growth Talent Group, I've spent a decade watching careers flourish in this space. Here's your complete roadmap to building a successful strata management career.

## Understanding the Career Pathway

### Entry Level: Assistant Strata Manager

**Typical Responsibilities:**
- Supporting senior managers with administrative tasks
- Coordinating basic maintenance and repairs
- Preparing meeting notices and agendas
- Managing correspondence with owners and tenants
- Updating building records and databases

**Salary Range:** $50,000 - $62,000

**Time in Role:** 1-2 years

**Success Factors:**
- Strong organisational skills
- Attention to detail
- Willingness to learn
- Good communication abilities

### Mid-Level: Strata Manager

**Typical Responsibilities:**
- Managing portfolio of 50-80 buildings independently
- Financial management and budget preparation
- Conducting AGMs and committee meetings
- Resolving owner and tenant disputes
- Ensuring building compliance with regulations

**Salary Range:** $65,000 - $95,000

**Time in Role:** 3-7 years

**Success Factors:**
- Technical knowledge and qualifications
- Strong stakeholder management
- Financial literacy
- Problem-solving capabilities

### Senior Level: Senior Strata Manager

**Typical Responsibilities:**
- Managing complex portfolios (100-150 buildings)
- High-value commercial and residential buildings
- Mentoring junior staff
- Building defect and remediation projects
- Committee relationship management

**Salary Range:** $95,000 - $125,000

**Time in Role:** 5-10 years

**Success Factors:**
- Deep technical expertise
- Leadership abilities
- Complex problem-solving
- Business development skills

### Executive Level: Portfolio Manager / Department Head

**Typical Responsibilities:**
- Team leadership and development
- Strategic business planning
- Client relationship management
- P&L responsibility
- New business development

**Salary Range:** $120,000 - $160,000+

**Time in Role:** 10+ years industry experience (this refers to individual career progression)

**Success Factors:**
- Proven leadership track record
- Business acumen
- Strategic thinking
- People management expertise

## Essential Qualifications

### Certificate IV in Strata Community Management

**What it covers:**
- Body corporate management
- Financial management
- Maintenance coordination
- Meeting procedures
- Legislation and compliance

**Where to study:**
- TAFE NSW, Queensland, Victoria
- SCA Institute
- Various online providers

**Time commitment:** 6-12 months (part-time)

**Cost:** $3,000 - $5,000 (often employer-sponsored)

**Is it essential?** While not legally required in all states, it's increasingly expected by employers and provides crucial foundational knowledge.

### Diploma of Strata Community Management

**When to pursue:** After 2-3 years as a Strata Manager

**Benefits:**
- Deeper technical knowledge
- Enhanced career prospects
- Preparation for senior roles
- Industry credibility

### State-Specific Licensing

Requirements vary by state:

**NSW:** Strata Manager Agent Licence (requires Certificate IV)
**Queensland:** Body Corporate Manager Licence
**Victoria:** No specific licence required
**WA:** Strata Management Agency Licence

**Research your state requirements** and ensure compliance early in your career.

## Critical Skills for Success

### 1. Financial Management

Strata managers are financial stewards for significant assets.

**Key Competencies:**
- Budget preparation and forecasting
- Financial report interpretation
- Levy setting and management
- Sinking fund planning
- Arrears management
- Tender evaluation

**How to Develop:**
- Take accounting or bookkeeping courses
- Learn Excel advanced functions
- Study sinking fund projections
- Work closely with accountants

### 2. Legislative Knowledge

Laws governing strata are complex and vary by state.

**Must-Know Areas:**
- Strata/Body Corporate Acts (state-specific)
- By-laws creation and enforcement
- Meeting procedures
- Owner rights and obligations
- Dispute resolution processes
- Building codes and safety regulations

**Stay Current:**
- Subscribe to SCA updates
- Attend industry seminars
- Join professional forums
- Read case law summaries

### 3. Communication and Diplomacy

You'll mediate between diverse stakeholders with competing interests.

**Scenarios You'll Face:**
- Owner disputes over noise, parking, pets
- Committee conflicts and politics
- Contractor negotiations
- Tenant complaints
- Emergency situations

**Develop This Skill:**
- Active listening practice
- Conflict resolution training
- Written communication courses
- Public speaking (for AGMs)

### 4. Project Management

Major works and building projects require coordination skills.

**Common Projects:**
- Building defect remediation
- Painting and maintenance programs
- Fire safety upgrades
- Common area renovations
- Energy efficiency improvements

**Build Expertise:**
- Understand construction basics
- Learn contract management
- Develop vendor relationships
- Study project planning methodologies

### 5. Technology Proficiency

Modern strata management is digital-first.

**Essential Systems:**
- Strata management software (StrataMax, REST, MRI)
- Microsoft Office suite (especially Excel)
- Digital communication platforms
- Cloud-based document management
- Financial systems integration

**Stay Relevant:**
- Embrace new technologies quickly
- Seek training on software updates
- Explore PropTech innovations
- Become the tech expert in your team

## Building Your Professional Network

### Join Industry Associations

**Strata Community Australia (SCA):**
- State-based chapters
- Professional development events
- Networking opportunities
- Industry advocacy
- Resources and publications

**Benefits of Membership:**
- Professional credibility
- Access to training
- Networking events
- Industry updates
- Career opportunities

### Attend Industry Events

Regular participation in:
- Annual SCA conferences
- State-based networking events
- Professional development workshops
- Supplier expos
- Legislative update sessions

**Why It Matters:**
Your network opens doors to opportunities, provides support during challenges, and keeps you informed of industry developments.

### LinkedIn and Online Presence

Build your professional brand:
- Complete, professional LinkedIn profile
- Share industry articles and insights
- Engage with industry leaders
- Join relevant groups
- Showcase achievements and qualifications

## Career Acceleration Strategies

### 1. Seek Mentorship

Find experienced strata managers who can:
- Share knowledge and insights
- Provide career guidance
- Introduce you to contacts
- Offer different perspectives

**Where to Find Mentors:**
- Within your company
- Through SCA networking
- LinkedIn connections
- Industry events

### 2. Specialise Strategically

Consider developing expertise in:

**High-Demand Specialisations:**
- Building defects and remediation
- Commercial strata
- Large-scale residential (100+ lots)
- Community title schemes
- Mixed-use developments

**Benefits:**
- Higher salaries
- More interesting work
- Competitive advantage
- Industry recognition

### 3. Develop Business Development Skills

Senior roles require client acquisition abilities:

**Learn:**
- Networking and relationship building
- Pitch and presentation skills
- Market understanding
- Competitive analysis
- Value proposition articulation

**Practice:**
- Attend networking events
- Support seniors on pitches
- Understand what wins clients
- Build your own relationships

### 4. Embrace Leadership Early

Don't wait for a senior role to develop leadership:

**Opportunities:**
- Mentor new staff
- Lead internal projects
- Volunteer for committee roles
- Present at team meetings
- Represent your company at events

### 5. Stay Qualified and Current

**Continuous Professional Development:**
- Complete annual CPD requirements
- Attend training sessions
- Stay updated on legislation
- Read industry publications
- Pursue additional qualifications

## Navigating Career Transitions

### Moving Between Companies

**When to Consider a Move:**
- Limited growth opportunities
- Portfolio size concerns (too large/too small)
- Better cultural fit elsewhere
- Significant salary increases
- Specialisation opportunities
- Geographic preferences

**How to Move Smartly:**
- Build your network before needing it
- Keep qualifications current
- Maintain professional reputation
- Give appropriate notice
- Leave on good terms (industry is small)

### Transitioning to Senior Roles

**Preparation Required:**
- Demonstrate consistent performance
- Build leadership capabilities
- Show business development potential
- Develop strategic thinking
- Pursue additional qualifications

**Timeline:**
Typically 5-7 years from entry to senior role, though exceptional performers may progress faster.

### Starting Your Own Strata Company

**Considerations:**
- Significant capital required
- Client acquisition challenges
- Regulatory requirements
- Staffing needs
- Technology investments

**When to Consider:**
- 10+ years industry experience
- Strong client relationships
- Financial stability
- Business acumen
- Risk tolerance

## Avoiding Career Pitfalls

### 1. Stagnation

**Risk:** Becoming comfortable and stopping learning

**Solution:** Set annual development goals, seek new challenges, pursue qualifications

### 2. Burnout

**Risk:** Excessive workload, poor boundaries

**Solution:** Sustainable portfolio sizes, time management, work-life balance prioritisation

### 3. Reputation Damage

**Risk:** Cutting corners, poor client service, unethical behaviour

**Solution:** Maintain professional standards always, industry is small and reputations matter

### 4. Technology Resistance

**Risk:** Falling behind as industry digitises

**Solution:** Embrace technology, continuous learning, stay current

## The Future of Strata Management Careers

**Positive Indicators:**
- Growing demand (increasing strata properties)
- Skills shortages (more opportunities)
- Rising salaries (5-8% annual growth)
- Technology enhancement (not replacement)
- Industry professionalisation

**Prepare for:**
- Increased technology requirements
- More complex buildings (defects, mixed-use)
- Enhanced regulatory environment
- Higher client expectations
- Sustainability focus

## Your Action Plan

**Year 1-2:**
- Secure entry-level role
- Complete Certificate IV
- Build foundational skills
- Learn from experienced managers
- Join SCA

**Year 3-5:**
- Transition to independent portfolio
- Develop specialisation area
- Pursue Diploma
- Build professional network
- Seek mentorship

**Year 5-10:**
- Progress to senior roles
- Develop leadership skills
- Build business development capability
- Mentor junior staff
- Consider specialisation deepening

**Year 10+:**
- Executive or ownership pathway
- Industry leadership roles
- Specialisation expertise
- Strategic business contribution

## Final Thoughts

Strata management offers genuine career satisfaction: variety, challenge, client relationships, problem-solving, and the satisfaction of maintaining people's homes and investments.

Success requires commitment to continuous learning, relationship building, and professional development. The pathway is clear, the opportunities are real, and the rewards—financial and professional—are substantial.

**Ready to start or advance your strata management career?** [Contact our specialist team](/contact) for personalised career guidance and access to exclusive opportunities.

*Steven Falkiner is Director and Head of Strata at Growth Talent Group with 10 years of experience in property and strata recruitment.*
    `,
    author: 'Steven Falkiner',
    category: 'Strata Management',
    date: '2025-03-05',
    readTime: '9 min read',
    image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=500&fit=crop',
    featured: false
  }
];

export const mockJobs = [
  {
    id: '1',
    title: 'Senior Property Manager',
    location: 'Sydney, NSW',
    type: 'Full-time',
    salary: '$80,000 - $100,000',
    category: 'Property Management',
    description: 'Leading property management firm seeking an experienced Senior Property Manager to oversee a diverse portfolio of residential and commercial properties.',
    requirements: [
      '5+ years property management experience',
      'Strong client relationship skills',
      'NSW Real Estate Licence',
      'Excellent organisational abilities'
    ],
    postedDate: '2025-01-10',
    featured: true
  },
  {
    id: '2',
    title: 'Strata Manager',
    location: 'Melbourne, VIC',
    type: 'Full-time',
    salary: '$70,000 - $90,000',
    category: 'Strata Management',
    description: 'Exciting opportunity for a qualified Strata Manager to join our growing team. Manage multiple schemes and deliver exceptional service to owners corporations.',
    requirements: [
      'Certificate IV in Strata Community Management',
      '3+ years strata management experience',
      'Strong communication skills',
      'Proficiency in strata management software'
    ],
    postedDate: '2025-01-08',
    featured: true
  },
  {
    id: '3',
    title: 'Real Estate Sales Agent',
    location: 'Brisbane, QLD',
    type: 'Full-time',
    salary: '$60,000 + Commission',
    category: 'Sales',
    description: 'Join a dynamic sales team and help clients buy and sell properties across Brisbane. Great earning potential for motivated individuals.',
    requirements: [
      'QLD Real Estate Licence',
      'Proven sales track record',
      'Excellent negotiation skills',
      'Self-motivated and results-driven'
    ],
    postedDate: '2025-01-05',
    featured: false
  },
  {
    id: '4',
    title: 'Commercial Property Analyst',
    location: 'Perth, WA',
    type: 'Full-time',
    salary: '$75,000 - $95,000',
    category: 'Analysis',
    description: 'Seeking an analytical professional to assess commercial property opportunities and provide strategic insights to our investment team.',
    requirements: [
      'Bachelor degree in Finance or Property',
      'Strong analytical skills',
      '2+ years commercial property experience',
      'Advanced Excel skills'
    ],
    postedDate: '2025-01-03',
    featured: false
  },
  {
    id: '5',
    title: 'Assistant Strata Manager',
    location: 'Adelaide, SA',
    type: 'Full-time',
    salary: '$55,000 - $65,000',
    category: 'Strata Management',
    description: 'Entry-level opportunity for someone looking to start their career in strata management. Full training provided.',
    requirements: [
      'Certificate IV in Strata Management (or working towards)',
      'Strong administrative skills',
      'Customer service experience',
      'Attention to detail'
    ],
    postedDate: '2025-01-01',
    featured: false
  },
  {
    id: '6',
    title: 'Property Portfolio Manager',
    location: 'Gold Coast, QLD',
    type: 'Full-time',
    salary: '$85,000 - $110,000',
    category: 'Property Management',
    description: 'Lead and manage a team of property managers while overseeing a premium portfolio of investment properties.',
    requirements: [
      '7+ years property management experience',
      'Leadership and team management skills',
      'QLD Real Estate Licence',
      'Strategic thinking abilities'
    ],
    postedDate: '2024-12-28',
    featured: true
  }
];

export const mockTeam = [
  {
    id: '1',
    name: 'Brandon Wylie',
    position: 'Director | Recruitment Agent',
    bio: '0418 201 771',
    fullBio: 'Brandon is a founding Director of Growth Talent Group with over 15 years of experience in property recruitment. He specialises in placing top-tier talent across real estate sales, property management, and strata management sectors. Brandon\'s industry expertise and extensive network have helped hundreds of professionals advance their careers while enabling organisations to build high-performing teams.',
    email: 'brandon@growthtalentgroup.com.au',
    phone: '0418 201 771',
    image: 'https://ui-avatars.com/api/?name=Brandon+Wylie&size=400&background=30D5C8&color=fff&bold=true',
    linkedin: 'https://linkedin.com',
    specialties: ['Executive Search', 'Real Estate Sales', 'Property Management', 'Team Building']
  },
  {
    id: '2',
    name: 'Madeleine Mulholland',
    position: 'Director | Recruitment Agent',
    bio: '0404 253 164',
    fullBio: 'Madeleine is a Director at Growth Talent Group, bringing 5 years of experience with exceptional relationship-building skills and a deep understanding of the property sector. With a background in real estate and recruitment, she excels at matching candidates with roles where they can truly thrive. Madeleine is known for her dedication to client service and her ability to understand both employer and candidate needs.',
    email: 'madeleine@growthtalentgroup.com.au',
    phone: '0404 253 164',
    image: 'https://ui-avatars.com/api/?name=Madeleine+Mulholland&size=400&background=30D5C8&color=fff&bold=true',
    linkedin: 'https://linkedin.com',
    specialties: ['Property Management', 'Candidate Relations', 'Client Partnerships', 'Career Consulting']
  },
  {
    id: '3',
    name: 'Steven Falkiner',
    position: 'Director | Head of Strata',
    bio: '0468 519 039',
    fullBio: 'Steven leads our Strata Management division with 10 years of extensive experience in both strata management and recruitment. His unique dual expertise allows him to identify the specific skills and qualities needed for success in strata roles. Steven has built strong relationships with leading strata management companies across Australia and understands the complexities of the sector inside-out.',
    email: 'steven@growthtalentgroup.com.au',
    phone: '0468 519 039',
    image: 'https://ui-avatars.com/api/?name=Steven+Falkiner&size=400&background=30D5C8&color=fff&bold=true',
    linkedin: 'https://linkedin.com',
    specialties: ['Strata Management', 'Leadership Roles', 'Industry Specialist', 'Client Development']
  },
  {
    id: '4',
    name: 'Shaun Millwood',
    position: 'Recruitment Agent',
    bio: '0481 822 809',
    fullBio: 'Shaun is a skilled Recruitment Agent with 6 years of experience focused on property management and real estate sales placements. His energetic approach and commitment to finding the right fit for both candidates and clients has earned him a reputation for delivering results. Shaun works closely with candidates throughout the entire recruitment journey.',
    email: 'shaun@growthtalentgroup.com.au',
    phone: '0481 822 809',
    image: 'https://ui-avatars.com/api/?name=Shaun+Millwood&size=400&background=30D5C8&color=fff&bold=true',
    linkedin: 'https://linkedin.com',
    specialties: ['Property Management', 'Real Estate Sales', 'Candidate Sourcing', 'Market Knowledge']
  },
  {
    id: '5',
    name: 'Kya Roberts',
    position: 'Recruitment Agent',
    bio: '0421 654 578',
    fullBio: 'Kya is a dedicated Recruitment Agent bringing enthusiasm and strong relationship-building skills to the team. She specialises in connecting talented property professionals with leading organisations across the real estate and property management sectors.',
    email: 'kya@growthtalentgroup.com.au',
    phone: '0421 654 578',
    image: 'https://ui-avatars.com/api/?name=Kya+Roberts&size=400&background=30D5C8&color=fff&bold=true',
    linkedin: 'https://linkedin.com',
    specialties: ['Property Recruitment', 'Candidate Relations', 'Client Matching', 'Market Knowledge']
  },
  {
    id: '6',
    name: 'Michelle Duhina',
    position: 'EA to Brandon Wylie',
    bio: '1300 GROWTH',
    fullBio: 'Michelle is the Executive Assistant to Brandon Wylie, providing crucial administrative and operational support to ensure smooth business operations. Her organisational skills and attention to detail keep the team running efficiently, and she is often the first point of contact for clients and candidates.',
    email: 'michelle@growthtalentgroup.com.au',
    phone: '1300 474684',
    image: 'https://ui-avatars.com/api/?name=Michelle+Duhina&size=400&background=30D5C8&color=fff&bold=true',
    linkedin: 'https://linkedin.com',
    specialties: ['Administration', 'Operations', 'Client Coordination', 'Office Management']
  }
];

export const mockTestimonials = [
  {
    id: '1',
    name: 'Melanie Yachmennikov',
    position: 'Strata Professional',
    content: 'Growth Talent Group helped place me in a role and the whole process was smooth from start to finish. The team was really supportive and made sure I felt looked after throughout. The strata company they connected me with has been great to work with as well. Really appreciate the professionalism and care — highly recommend!',
    company: 'Google Review',
    rating: 5
  },
  {
    id: '2',
    name: 'Evelyn May',
    position: 'Real Estate Professional',
    content: 'From the very start, Madeleine\'s service has been absolutely amazing and so incredibly helpful. She truly took the time to understand what I was looking for and helped me find the right agency for me. She was always available to talk, even on weekends, and consistently went above and beyond to support me throughout the entire process.',
    company: 'Google Review',
    rating: 5
  },
  {
    id: '3',
    name: 'Ahmad Hamdy',
    position: 'Real Estate Professional',
    content: 'Amazing work from Madeleine and Brandon! Found me my dream role, I would definitely recommend them 20x over.',
    company: 'Google Review',
    rating: 5
  },
  {
    id: '4',
    name: 'Tenzin Tsega',
    position: 'Real Estate Professional',
    content: 'I\'ve worked with many recruiters over the years, but I can honestly say Brandon is in a league of his own. From day one, he\'s genuinely had my best interests at heart, taking the time to truly listen, understand my goals, and guide me toward the right opportunities. Brandon, thank you for everything you\'ve done for me thus far, you\'re truly one of a kind.',
    company: 'Google Review',
    rating: 5
  },
  {
    id: '5',
    name: 'Garth Makowski',
    position: 'Principal',
    content: 'Brandon first & foremost is an honest man, a straight shooter & a good bloke. Early on Brandon made the effort to understand our business, our culture, our leadership style. He has placed many team members within our business with success. I\'d highly recommend Brandon to other business owners, especially where you are looking for a personalised recruitment service.',
    company: 'Harcourts The Property People',
    rating: 5
  },
  {
    id: '6',
    name: 'Elizabeth Bowen',
    position: 'Real Estate Professional',
    content: 'Stop now. Call Brandon. Brandon hustled for me like I\'ve never seen before, got me in front of the right people quickly, and was very committed and generous with his time. It was especially helpful as I was new to real estate and navigating a lot of unknown territory. He advocated, negotiated, and made it happen.',
    company: 'Google Review',
    rating: 5
  },
  {
    id: '7',
    name: 'Alana Divan',
    position: 'Real Estate Professional',
    content: 'I had an excellent experience working with Brandon from Growth Talent Group. Brandon\'s communication was always clear, honest, and timely. He genuinely took the time to understand both my experience and what the employer was looking for. It never felt transactional as he was supportive, transparent, and easy to deal with throughout the entire process.',
    company: 'Google Review',
    rating: 5
  },
  {
    id: '8',
    name: 'Sarah McQueen',
    position: 'Real Estate Professional',
    content: 'Maddie is amazing only agent that called me back and helped me out. Always there to answer my questions and talk me through everything. She has been so good to deal with and helped me find a great job. Could not have done it without her.',
    company: 'Google Review',
    rating: 5
  },
  {
    id: '9',
    name: 'Dejan Gajic',
    position: 'Real Estate Professional',
    content: 'Absolute Superstars as found me the dream job I was always hoping for. Maddie and Brandon your the best so thank you so much for the amazing hard work both you did to get me that foot in the door. Also your both great and wonderful people which is a rarity to find nowadays.',
    company: 'Google Review',
    rating: 5
  }
];

export const mockClients = [
  {
    name: 'Highland Property Group',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/3bppzmwl_HPA.png'
  },
  {
    name: 'Ray White',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/c1bctdwe_image.png'
  },
  {
    name: 'Raine & Horne',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/3dtizluq_image.png'
  },
  {
    name: 'Stone Real Estate',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/pgunlvmr_image.png'
  },
  {
    name: 'Ausrealty',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/6cjoioig_image.png'
  },
  {
    name: 'Forsyth Real Estate',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/hi110hdt_image.png'
  },
  {
    name: 'AYRE Real Estate',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/eclqtdp8_1080-logo.webp'
  },
  {
    name: 'One Agency',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/68ecd003_images%20%282%29.png'
  },
  {
    name: 'Harcourts',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/tg4jgqsi_images.jpeg'
  },
  {
    name: 'Pace Property Agents',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/lkl48zee_image.png'
  },
  {
    name: 'McConnell Bourn',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/x5zaa1mx_image.png'
  },
  {
    name: 'McGrath Estate Agents',
    logo: 'https://customer-assets.emergentagent.com/job_strata-specialists/artifacts/xt8vx1vs_images%20%283%29.png'
  }
];

export const mockServices = [
  {
    id: '1',
    title: 'Property Management Recruitment',
    icon: 'Building2',
    description: 'Specialist recruitment for residential and commercial property management roles at all levels, from Property Managers to Department Heads.',
    benefits: [
      'Extensive network of property professionals',
      'Deep understanding of property management skills',
      'Quick turnaround times',
      'Nationwide coverage'
    ]
  },
  {
    id: '2',
    title: 'Strata Management Recruitment',
    icon: 'Users',
    description: 'Expert placement of strata managers, assistant managers, and strata support staff across Australia.',
    benefits: [
      'Specialised knowledge of strata sector',
      'Access to certified professionals',
      'Understanding of state-specific requirements',
      'Long-term candidate relationships'
    ]
  },
  {
    id: '3',
    title: 'Real Estate Sales & BDM',
    icon: 'TrendingUp',
    description: 'Connecting talented sales professionals and Business Development Managers with leading real estate agencies for residential and commercial opportunities.',
    benefits: [
      'Performance-driven candidate assessment',
      'Track record verification',
      'Cultural fit evaluation',
      'Ongoing support post-placement'
    ]
  },
  {
    id: '4',
    title: 'Executive Search',
    icon: 'Award',
    description: 'Confidential executive search services for senior leadership roles including General Managers, Sales Managers, Department Heads, and C-suite positions in property and real estate sectors.',
    benefits: [
      'Headhunting for top-tier talent',
      'Executive-level candidate network',
      'Confidential search processes',
      'Leadership assessment expertise'
    ]
  },
  {
    id: '5',
    title: 'Rent Roll Acquisition',
    icon: 'TrendingUp',
    description: 'Strategic rent roll search and acquisition services for property management businesses looking to expand their portfolio. We connect quality sellers with qualified buyers, facilitating seamless transitions that drive business growth.',
    benefits: [
      'Exclusive access to off-market rent rolls',
      'Comprehensive due diligence support',
      'Confidential buyer-seller matching',
      'Business valuation expertise',
      'Post-acquisition integration guidance'
    ]
  }
];

export const mockExecutiveRoles = [
  {
    id: '1',
    title: 'General Manager - Property',
    seniority: 'Executive',
    description: 'Strategic leadership role overseeing all property management operations'
  },
  {
    id: '2',
    title: 'Sales Manager - Real Estate',
    seniority: 'Management',
    description: 'Lead and develop high-performing sales teams across multiple locations'
  },
  {
    id: '3',
    title: 'Department Head - Property Management',
    seniority: 'Management',
    description: 'Drive departmental strategy and operational excellence'
  },
  {
    id: '4',
    title: 'Chief Operations Officer',
    seniority: 'Executive',
    description: 'Oversee company-wide operations and strategic initiatives'
  },
  {
    id: '5',
    title: 'Sales Director',
    seniority: 'Executive',
    description: 'Set sales strategy and drive revenue growth across the organisation'
  },
  {
    id: '6',
    title: 'Head of Strata Management',
    seniority: 'Management',
    description: 'Lead strata division and develop best-in-class service delivery'
  }
];
